import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BACKEND_URL } from "src/api/endpoints";
import {
  AgentsModuleCreateRequest,
  FlowApiSchemasV2ModulesAgentsAgentsModuleSpec as AgentsModuleSpec,
  AgentsModuleUpdateRequest,
  CaseManagementModuleCreateRequest,
  FlowApiSchemasV2ModulesCaseManagementCaseManagementModuleSpec as CaseManagementModuleSpec,
  CaseManagementModuleUpdateRequest,
  Configuration,
  ConnectModuleCreateRequest,
  FlowApiSchemasV2ModulesConnectConnectModuleSpec as ConnectModuleSpec,
  ConnectModuleUpdateRequest,
  FilesModuleCreateRequest,
  FlowApiSchemasV2ModulesFilesFilesModuleSpec as FilesModuleSpec,
  FilesModuleUpdateRequest,
  HistoryModuleCreateRequest,
  FlowApiSchemasV2ModulesHistoryHistoryModuleSpec as HistoryModuleSpec,
  HistoryModuleUpdateRequest,
  Modules,
  WorkspaceDb,
  WorkspacesV2Api,
} from "src/clients/flow-api";

export type ModuleUpdateRequest =
  | AgentsModuleUpdateRequest
  | FilesModuleUpdateRequest
  | CaseManagementModuleUpdateRequest
  | ConnectModuleUpdateRequest
  | HistoryModuleUpdateRequest;

// Create WorkspacesV2Api instance
const workspacesV2Api = new WorkspacesV2Api(
  new Configuration({
    basePath: BACKEND_URL,
  }),
);

const DEFAULT_STALE_TIME = 1000 * 60 * 5; // 5 minutes

// Query keys for modules
export const workspacesV2Keys = {
  workspaces: () => ["workspacesV2"] as const,
  workspace: (workspaceId: string) => ["workspacesV2", workspaceId] as const,
  workspaceModules: (workspaceId: string) =>
    ["workspacesV2", workspaceId, "modules"] as const,
  workspaceModule: (workspaceId: string, moduleType: string) =>
    ["workspacesV2", workspaceId, "module", moduleType] as const,
};

// Helper function to invalidate workspace-related queries
const invalidateWorkspaceQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  moduleType: string,
) => {
  queryClient.invalidateQueries({
    queryKey: workspacesV2Keys.workspaces(),
  });
  queryClient.invalidateQueries({
    queryKey: workspacesV2Keys.workspace(workspaceId),
  });
  queryClient.invalidateQueries({
    queryKey: workspacesV2Keys.workspaceModules(workspaceId),
  });
  queryClient.invalidateQueries({
    queryKey: workspacesV2Keys.workspaceModule(workspaceId, moduleType),
  });
};

// Hook to get all workspaces with modules using v2 API
export const useLegacyWorkspaces = () => {
  return useQuery<WorkspaceDb[], Error>({
    queryKey: workspacesV2Keys.workspaces(),
    queryFn: async () => {
      const response = await workspacesV2Api.getWorkspacesApiV2WorkspacesGet();
      return response.data;
    },
    staleTime: DEFAULT_STALE_TIME,
  });
};

// Hook to get specific workspace with modules
export const useLegacyWorkspace = (workspaceId?: string) => {
  return useQuery<WorkspaceDb, Error>({
    queryKey: workspacesV2Keys.workspace(workspaceId!),
    queryFn: async () => {
      const response =
        await workspacesV2Api.getWorkspaceApiV2WorkspacesWorkspaceIdGet(
          workspaceId!,
        );
      return response.data;
    },
    enabled: !!workspaceId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

// Hook to get modules for a specific workspace
export const useLegacyWorkspaceModules = (workspaceId?: string) => {
  return useQuery<Modules, Error>({
    queryKey: workspacesV2Keys.workspaceModules(workspaceId!),
    queryFn: async () => {
      const response =
        await workspacesV2Api.getModulesApiV2WorkspacesWorkspaceIdModulesGet(
          workspaceId!,
        );
      return response.data;
    },
    enabled: !!workspaceId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

// Hook to get a specific module
export const useLegacyWorkspaceModule = (
  workspaceId?: string,
  moduleType?: "agents" | "files" | "case_management" | "connect" | "history",
) => {
  return useQuery<
    | AgentsModuleSpec
    | FilesModuleSpec
    | CaseManagementModuleSpec
    | ConnectModuleSpec
    | HistoryModuleSpec,
    Error
  >({
    queryKey: workspacesV2Keys.workspaceModule(workspaceId!, moduleType!),
    queryFn: async () => {
      if (moduleType === "agents") {
        const response =
          await workspacesV2Api.getAgentsModuleApiV2WorkspacesWorkspaceIdModulesAgentsGet(
            workspaceId!,
          );
        return response.data;
      } else if (moduleType === "files") {
        const response =
          await workspacesV2Api.getFilesModuleApiV2WorkspacesWorkspaceIdModulesFilesGet(
            workspaceId!,
          );
        return response.data;
      } else if (moduleType === "case_management") {
        const response =
          await workspacesV2Api.getCaseManagementModuleApiV2WorkspacesWorkspaceIdModulesCaseManagementGet(
            workspaceId!,
          );
        return response.data;
      } else if (moduleType === "connect") {
        const response =
          await workspacesV2Api.getConnectModuleApiV2WorkspacesWorkspaceIdModulesConnectGet(
            workspaceId!,
          );
        return response.data;
      } else {
        const response =
          await workspacesV2Api.getHistoryModuleApiV2WorkspacesWorkspaceIdModulesHistoryGet(
            workspaceId!,
          );
        return response.data;
      }
    },
    enabled: !!workspaceId && !!moduleType,
    staleTime: DEFAULT_STALE_TIME,
  });
};

// Hook to create/install an `agents` module
export const useCreateAgentsModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: AgentsModuleCreateRequest;
    }) => {
      const response =
        await workspacesV2Api.createAgentsModuleApiV2WorkspacesWorkspaceIdModulesAgentsPut(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "agents");
    },
  });
};

// Hook to create/install a files module
export const useCreateFilesModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: FilesModuleCreateRequest;
    }) => {
      const response =
        await workspacesV2Api.createFilesModuleApiV2WorkspacesWorkspaceIdModulesFilesPut(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "files");
    },
  });
};

// Hook to create/install a case management module
export const useCreateCaseManagementModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: CaseManagementModuleCreateRequest;
    }) => {
      const response =
        await workspacesV2Api.createCaseManagementModuleApiV2WorkspacesWorkspaceIdModulesCaseManagementPut(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(
        queryClient,
        variables.workspaceId,
        "case_management",
      );
    },
  });
};

// Hook to create/install a connect module
export const useCreateConnectModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: ConnectModuleCreateRequest;
    }) => {
      const response =
        await workspacesV2Api.createConnectModuleApiV2WorkspacesWorkspaceIdModulesConnectPut(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "connect");
    },
  });
};

// Hook to create/install a history module
export const useCreateHistoryModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: HistoryModuleCreateRequest;
    }) => {
      const response =
        await workspacesV2Api.createHistoryModuleApiV2WorkspacesWorkspaceIdModulesHistoryPut(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "history");
    },
  });
};

// Hook to update an `agents` module
export const useUpdateAgentsModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: AgentsModuleUpdateRequest;
    }) => {
      const response =
        await workspacesV2Api.updateAgentsModuleApiV2WorkspacesWorkspaceIdModulesAgentsPatch(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "agents");
    },
  });
};

// Hook to update a files module
export const useUpdateFilesModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: FilesModuleUpdateRequest;
    }) => {
      const response =
        await workspacesV2Api.updateFilesModuleApiV2WorkspacesWorkspaceIdModulesFilesPatch(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "files");
    },
  });
};

// Hook to update a case management module
export const useUpdateCaseManagementModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: CaseManagementModuleUpdateRequest;
    }) => {
      const response =
        await workspacesV2Api.updateCaseManagementModuleApiV2WorkspacesWorkspaceIdModulesCaseManagementPatch(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(
        queryClient,
        variables.workspaceId,
        "case_management",
      );
    },
  });
};

// Hook to update a connect module
export const useUpdateConnectModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: ConnectModuleUpdateRequest;
    }) => {
      const response =
        await workspacesV2Api.updateConnectModuleApiV2WorkspacesWorkspaceIdModulesConnectPatch(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "connect");
    },
  });
};

// Hook to update a history module
export const useUpdateHistoryModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: HistoryModuleUpdateRequest;
    }) => {
      const response =
        await workspacesV2Api.updateHistoryModuleApiV2WorkspacesWorkspaceIdModulesHistoryPatch(
          workspaceId,
          data,
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateWorkspaceQueries(queryClient, variables.workspaceId, "history");
    },
  });
};

// Generic hook to update any module type
export const useUpdateModule = () => {
  const updateAgentsModule = useUpdateAgentsModule();
  const updateFilesModule = useUpdateFilesModule();
  const updateCaseManagementModule = useUpdateCaseManagementModule();
  const updateConnectModule = useUpdateConnectModule();
  const updateHistoryModule = useUpdateHistoryModule();

  return {
    mutateAsync: async ({
      workspaceId,
      moduleType,
      data,
    }: {
      workspaceId: string;
      moduleType: string;
      data:
        | AgentsModuleUpdateRequest
        | FilesModuleUpdateRequest
        | CaseManagementModuleUpdateRequest
        | ConnectModuleUpdateRequest
        | HistoryModuleUpdateRequest;
    }) => {
      if (moduleType === "agents") {
        return await updateAgentsModule.mutateAsync({
          workspaceId,
          data: data as AgentsModuleUpdateRequest,
        });
      } else if (moduleType === "files") {
        return await updateFilesModule.mutateAsync({
          workspaceId,
          data: data as FilesModuleUpdateRequest,
        });
      } else if (moduleType === "case_management") {
        return await updateCaseManagementModule.mutateAsync({
          workspaceId,
          data: data as CaseManagementModuleUpdateRequest,
        });
      } else if (moduleType === "connect") {
        return await updateConnectModule.mutateAsync({
          workspaceId,
          data: data as ConnectModuleUpdateRequest,
        });
      } else if (moduleType === "history") {
        return await updateHistoryModule.mutateAsync({
          workspaceId,
          data: data as HistoryModuleUpdateRequest,
        });
      } else {
        throw new Error(`Unsupported module type: ${moduleType}`);
      }
    },
  };
};

// Generic hook to create any module type
export const useCreateModule = () => {
  const createAgentsModule = useCreateAgentsModule();
  const createFilesModule = useCreateFilesModule();
  const createCaseManagementModule = useCreateCaseManagementModule();
  const createConnectModule = useCreateConnectModule();
  const createHistoryModule = useCreateHistoryModule();

  return {
    isPending:
      createAgentsModule.isPending ||
      createFilesModule.isPending ||
      createCaseManagementModule.isPending ||
      createConnectModule.isPending ||
      createHistoryModule.isPending,
    mutateAsync: async ({
      workspaceId,
      moduleType,
      data,
    }: {
      workspaceId: string;
      moduleType: string;
      data?:
        | AgentsModuleCreateRequest
        | FilesModuleCreateRequest
        | CaseManagementModuleCreateRequest
        | ConnectModuleCreateRequest
        | HistoryModuleCreateRequest;
    }) => {
      // Use empty object as default data if none provided
      const moduleData = data || {};

      if (moduleType === "agents") {
        return await createAgentsModule.mutateAsync({
          workspaceId,
          data: moduleData as AgentsModuleCreateRequest,
        });
      } else if (moduleType === "files") {
        return await createFilesModule.mutateAsync({
          workspaceId,
          data: moduleData as FilesModuleCreateRequest,
        });
      } else if (moduleType === "case_management") {
        return await createCaseManagementModule.mutateAsync({
          workspaceId,
          data: moduleData as CaseManagementModuleCreateRequest,
        });
      } else if (moduleType === "connect") {
        return await createConnectModule.mutateAsync({
          workspaceId,
          data: moduleData as ConnectModuleCreateRequest,
        });
      } else if (moduleType === "history") {
        return await createHistoryModule.mutateAsync({
          workspaceId,
          data: moduleData as HistoryModuleCreateRequest,
        });
      } else {
        throw new Error(`Unsupported module type: ${moduleType}`);
      }
    },
  };
};

export type ModuleSpec =
  | AgentsModuleSpec
  | FilesModuleSpec
  | CaseManagementModuleSpec
  | ConnectModuleSpec
  | HistoryModuleSpec;
